import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";

import useTranslation from "next-translate/useTranslation";
import loadNamespaces from "next-translate/loadNamespaces";
import { useCompleoCareerPage } from "customHooks/useCompleoCareerPage";
import { createMuiTheme } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import DefaultLayout from "components/_Layout/Default";
import toUpper from "lodash/toUpper";
import toString from "lodash/toString";
import { apiDirectCall } from "customHooks/useApi/api";
import { useSession } from "next-auth/react";
import Head from "next/head";
import GoogleAnalytics from "components/Analytics/GA";
import GoogleTagManager from "components/Analytics/GTM";
import MetaPixel from "components/Analytics/MetaPixel";
//import * as Sentry from "@sentry/nextjs";
import { getCookie } from "cookies-next";
import { getJsonLd } from "utils/jsonLDUtils";
//import { getImageSize } from "functions/util";

const JobDetail = (props: any) => {
    const {
        data,
        companyId,
        company,
        theme,
        jobViewData,
        languages,
        enableMultipleApplicationsPerJob,
        companyPrivacyPolicyUrl,
        favicon,
    } = props;

    const cookieIsCustomDomain = getCookie("x-custom-domain");
    const themeObject = createMuiTheme(theme);
    const router = useRouter();
    const { locale } = router;

    let { uniqueLink, source, utm_source } = router.query;
    uniqueLink = toUpper(toString(uniqueLink));

    if (!source && utm_source) {
        console.log("Using utm_source as source", utm_source);
        source = utm_source === "google_jobs_apply" ? "CP22" : undefined;
    }

    const [hostname, setHostname] = useState("");

    useEffect(() => {
        if (window.location) {
            const localHostName = `${window.location.host}`;
            const protocol = !localHostName.includes("http")
                ? localHostName.includes("localhost")
                    ? "http://"
                    : "https://"
                : "";

            setHostname(protocol + localHostName);
        }
    }, []);
    const { data: session } = useSession();
    const user = session?.user?.email;
    const [shareImageSize, setShareImageSize] =
        useState<Compleo.IObject | null>(null);
    const metadata = data.metadado;
    const moduleName = "JOBDETAIL";
    const { lang } = useTranslation(moduleName);
    const language = locale ?? lang;

    useEffect(() => {
        if (localStorage) {
            const savePageView = async (jobId: string) => {
                try {
                    await apiDirectCall(
                        "/job/careeraddjobview",
                        "post",
                        {
                            companyId,
                            jobId,
                            source,
                            userId: user,
                        },
                        undefined,
                        undefined,
                        "none"
                    );
                } catch (ex) {
                    console.log(
                        "savePageView error",
                        ex?.response?.data?.message
                            ? ex.response.data.message
                            : ex
                    );
                    // Sentry.withScope(function (scope) {
                    //     scope.setContext("company", {
                    //         companyId: companyId,
                    //         uniqueLink: uniqueLink ? String(uniqueLink) : "",
                    //     });
                    //     scope.setTag(
                    //         "careersEventType",
                    //         "jobDetail_careeraddjobview"
                    //     );
                    //     scope.setUser({
                    //         email: user ?? "",
                    //     });
                    //     Sentry.captureException(
                    //         ex?.response?.data?.message
                    //             ? new Error(ex.response.data.message)
                    //             : ex
                    //     );
                    // });
                }
            };
            let pageViewDataStorage: Compleo.IObject = {};
            try {
                pageViewDataStorage = JSON.parse(
                    localStorage.getItem(
                        `CompleoCareersContext${companyId}_pageViewData`
                    ) || ""
                );
            } catch (error) {
                console.log("Error parsing pageViewDataStorage", error);
            }
            const jobId = jobViewData.pk.split(":")[1];
            if (
                jobId &&
                (!pageViewDataStorage ||
                    !pageViewDataStorage.jobs?.includes(jobId))
            ) {
                const newData = {
                    ...pageViewDataStorage,
                    jobs: pageViewDataStorage.jobs
                        ? [...pageViewDataStorage.jobs, jobId]
                        : [jobId],
                };

                localStorage.setItem(
                    `CompleoCareersContext${companyId}_pageViewData`,
                    JSON.stringify(newData)
                );

                savePageView(jobId);
            }
        }
    }, [jobViewData, source, user, companyId, uniqueLink]);

    useEffect(() => {
        if (localStorage && source) {
            let applicantDataStorage = {};

            try {
                applicantDataStorage = JSON.parse(
                    localStorage.getItem(
                        `CompleoCareersContext${companyId}_applicantData`
                    ) || ""
                );
            } catch (error) {
                console.log("Error parsing applicantDataStorage", error);
            }

            const newData = { ...applicantDataStorage, source };

            localStorage.setItem(
                `CompleoCareersContext${companyId}_applicantData`,
                JSON.stringify(newData)
            );
        }
    }, [source, companyId]);
    const pageResult = useCompleoCareerPage({
        language,
        moduleMetadata: metadata,
        moduleName,
        companyId,
        uniqueLink: toString(uniqueLink),
        router,
        initialData: { jobData: jobViewData, companyData: company },
        translationNamespace: ["JOBDETAIL"],
        showEditCareersButton: false,
        languages,
        enableMultipleApplicationsPerJob,
        companyPrivacyPolicyUrl,
        isCustomDomain: cookieIsCustomDomain === true,
    });

    const getTextFromHtml = (html: string) => {
        return html.replace(/<(?:.|\n)*?>/gm, "");
    };

    const pageHeadTitle =
        language === "pt-BR"
            ? `Vaga ${jobViewData.title} | Carreiras ${
                  company?.companyFancyName ?? company?.companyName ?? ""
              }`
            : `Job Detail: ${
                  jobViewData.translatedData[language]
                      ? jobViewData.translatedData[language]["title"]
                      : jobViewData.title
              } | Careers ${
                  company?.companyFancyName ?? company?.companyName ?? ""
              }`;

    const jsonLd = getJsonLd(jobViewData, language, company);

    return (
        <>
            <Head>
                <title>{pageHeadTitle}</title>
                <link rel="icon" href={favicon || "/assets/img/favicon.ico"} />
                <link
                    rel="canonical"
                    href={
                        cookieIsCustomDomain === true
                            ? `${hostname}/jobdetail/${
                                  jobViewData.pk.split(":")[1]
                              }`
                            : `${hostname}/${uniqueLink.toLowerCase()}/jobdetail/${
                                  jobViewData.pk.split(":")[1]
                              }`
                    }
                />
                <meta
                    property="og:title"
                    content={`Oportunidade para ${jobViewData.title}`}
                    key="jobTitle"
                />
                <meta
                    property="og:description"
                    content={getTextFromHtml(jobViewData.description)}
                    key="jobDescription"
                />
                {hostname && (
                    <meta
                        property="og:url"
                        content={
                            cookieIsCustomDomain === true
                                ? `${hostname}/jobdetail/${
                                      jobViewData.pk.split(":")[1]
                                  }`
                                : `${hostname}/${uniqueLink.toLowerCase()}/jobdetail/${
                                      jobViewData.pk.split(":")[1]
                                  }`
                        }
                        key="url"
                    />
                )}
                <meta property="og:type" content="website" key="website" />
                <meta
                    property="fb:app_id"
                    content={process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}
                />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
                />
                {jobViewData?.jobSocialMediaImage?.key && (
                    <>
                        <meta
                            property="og:image"
                            content={`${process.env.NEXT_PUBLIC_ASSETS_URL}/${jobViewData.jobSocialMediaImage.key}`}
                            key="jobSocialMediaImage"
                        />
                        {jobViewData?.jobSocialMediaImage?.width && (
                            <meta
                                property="og:image:width"
                                content={jobViewData.jobSocialMediaImage.width}
                                key="jobSocialMediaImageWidth"
                            />
                        )}
                        {jobViewData?.jobSocialMediaImage?.height && (
                            <meta
                                property="og:image:height"
                                content={jobViewData.jobSocialMediaImage.height}
                                key="jobSocialMediaImageHeight"
                            />
                        )}
                    </>
                )}
            </Head>

            <ThemeProvider theme={themeObject}>
                <DefaultLayout>{pageResult}</DefaultLayout>
            </ThemeProvider>
            {props.googleAnalyticsId && (
                <GoogleAnalytics gaTrackingId={props.googleAnalyticsId} />
            )}
            {props.googleTagManagerId && (
                <GoogleTagManager gtmId={props.googleTagManagerId} />
            )}
            {props.metaPixelId && <MetaPixel pixelId={props.metaPixelId} />}
        </>
    );
};

export async function getStaticPaths(props: any) {
    return { paths: [], fallback: "blocking" };
}

export async function getStaticProps(props: any) {
    const { params, locale, ctx } = props;
    const uniqueLink = toUpper(params.uniqueLink);
    const apiAddressRoot = process.env.NEXT_PUBLIC_REACT_APP_API_ADDRESS;
    const apiRequestBody = {
        modules: [
            {
                module: "JOBDETAIL",
                type: "careers",
            },
        ],
        uniqueLink: uniqueLink,
        jobId: params.id,
        modulesTranslation: [
            {
                module: "JOBDETAIL",
                type: "careers",
            },
        ],
        language: locale || "pt-BR",
    };
    const apiEndpoint = `${apiAddressRoot}/careers/getalldata`;
    const apiResponse = await fetch(apiEndpoint, {
        method: "post",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...apiRequestBody }),
    });
    const responseJson = await apiResponse.json();
    if (apiResponse.status !== 200) {
        if (
            apiResponse.status === 404 ||
            responseJson?.message == "Job not found"
        ) {
            return { notFound: true };
        }
        throw new Error(
            `Failed to fetch careers page data - API Response Status: ${
                apiResponse.status
            } - API Response Json ${JSON.stringify(responseJson)}`
        );
    }
    const languages = responseJson.languages;
    if (languages && !languages.includes(locale)) {
        return {
            notFound: true,
        };
    }
    const careerPageMetadata = responseJson.metadatas?.filter(
        (m: any) => m.module === "JOBDETAIL" && m.type === "careers"
    )[0];
    const data = careerPageMetadata?.data ?? {};
    const careerPageTranslation = responseJson.translations?.filter(
        (m: any) => m.module === "JOBDETAIL" && m.type === "careers"
    )[0];
    const jobViewData = responseJson.jobData?.job ?? {};
    if (jobViewData?.jobSocialMediaImage?.key) {
        const imageUrl = `${process.env.NEXT_PUBLIC_ASSETS_URL}/${jobViewData.jobSocialMediaImage.key}`;
        const imageSize = await getImageSize(imageUrl);
        if (imageSize && imageSize.width && imageSize.height) {
            jobViewData.jobSocialMediaImage.width =
                imageSize.width < 200 ? 200 : imageSize.width;
            jobViewData.jobSocialMediaImage.height =
                imageSize.height < 200 ? 200 : imageSize.height;
        } else {
            // Set default values if image size couldn't be determined
            jobViewData.jobSocialMediaImage.width = 200;
            jobViewData.jobSocialMediaImage.height = 200;
        }
    }
    const themeDB = responseJson.theme;
    const companyId = responseJson.companyId;
    const enableMultipleApplicationsPerJob =
        responseJson.enableMultipleApplicationsPerJob ?? false;
    const companyPrivacyPolicyUrl = responseJson.companyPrivacyPolicyUrl;
    const googleAnalyticsId = responseJson.googleAnalyticsId;
    const googleTagManagerId = responseJson.googleTagManagerId;
    const metaPixelId = responseJson.metaPixelId;
    const company = responseJson.company;
    return {
        props: {
            data,
            jobViewData,
            theme: themeDB?.theme ?? {},
            favicon: themeDB?.favicon ?? null,
            companyId,
            company,
            languages,
            enableMultipleApplicationsPerJob,
            shallow: true,
            companyPrivacyPolicyUrl: companyPrivacyPolicyUrl ?? null,
            googleAnalyticsId: googleAnalyticsId ?? null,
            googleTagManagerId: googleTagManagerId ?? null,
            metaPixelId: metaPixelId ?? null,
            ...(await loadNamespaces({
                ...ctx,
                pathname: "/jobdetail",
                loadLocaleFrom: (lang, ns) =>
                    Promise.resolve(careerPageTranslation.data),
            })),
        },
        revalidate: 300, //seconds
    };
}
const getImageSize = (imgUrl: string) => {
    return new Promise<any>((resolve, reject) => {
        try {
            const url = require("url");
            const https = require("https");
            const sizeOf = require("image-size");
            const options = url.parse(imgUrl);

            https
                .get(options, function (response: any) {
                    if (response.statusCode !== 200) {
                        console.error(
                            `Failed to fetch image: ${response.statusCode}`
                        );
                        resolve(null);
                        return;
                    }

                    const chunks: any[] = [];
                    response
                        .on("data", function (chunk: any) {
                            chunks.push(chunk);
                        })
                        .on("end", function () {
                            try {
                                const buffer = Buffer.concat(chunks);
                                const imgSize = sizeOf(buffer);
                                resolve({
                                    width: imgSize.width,
                                    height: imgSize.height,
                                });
                            } catch (error) {
                                console.error("Error processing image:", error);
                                resolve(null);
                            }
                        })
                        .on("error", function (error: Error) {
                            console.error("Error reading image stream:", error);
                            resolve(null);
                        });
                })
                .on("error", function (error: Error) {
                    console.error("Error fetching image:", error);
                    resolve(null);
                });
        } catch (error) {
            console.error("Error in getImageSize:", error);
            resolve(null);
        }
    });
};

export default JobDetail;
